<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>
    <warehouse-waybill-dialog v-model="waybillDialog" :edited-item="editedItem">
    </warehouse-waybill-dialog>

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="toLatin(group.warehouseName)"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['number']"
        >
          <template v-slot:item.document_date="{ item }">
            {{ localeDate(item.document_date) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-spacer></v-spacer>
            <circle-button
              @click="editItem(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)" />
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-receipt-text-arrow-right-outline"
        :title="$t('no_invoices')"
        :subtitle="$t('no_invoices_yet')"
      >
        <warehouse-button-waybill
          @click="waybillDialog = true"
        ></warehouse-button-waybill>
      </empty-state>

      <div class="fixed-bottom-button">
        <v-btn color="blue" block @click="waybillDialog = true" dark>
          {{ $t("warehouse.release") }}
          <v-icon class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        class="striped-table ordera-box-shadow"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer></v-spacer>

            <warehouse-button-waybill
              @click="waybillDialog = true"
            ></warehouse-button-waybill>
          </v-toolbar>
        </template>
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              @click="editItem(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)" />
          </v-row>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{
            toLatin(item.warehouse?.name) || "Nemamo dodat warehouse. Trebamo proveriti"
          }}
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-receipt-text-arrow-right-outline"
        :title="$t('no_invoices')"
        :subtitle="$t('no_invoices_yet')"
      >
        <warehouse-button-waybill
          @click="waybillDialog = true"
        ></warehouse-button-waybill>
      </empty-state>
    </template>
  </div>
</template>

<script>
import { WarehouseWaybillDialog } from "@/components/warehouse/requirement";
import { WarehouseButtonWaybill } from "@/components/warehouse/buttons";
import { waybillDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehouseTabInvoice",
  mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
  components: {
    WarehouseWaybillDialog,
    WarehouseButtonWaybill,
    EmptyState,
    MobileDataCards,
  },
  data: () => ({
    documents: [],
    waybillDialog: false,
    editedItem: waybillDocumentGrocery,
    loading: false,
  }),
  created() {
    this.load();
  },

  methods: {
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.waybillDialog = true;
    },

    deleteItem(item) {
      this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_issue"),
          this.$t("warehouse.confirm_delete_issue"),
          {
            color: "warning",
          }
        )
        .then(() => {
          this.documents = this.documents.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("invoice_deleted"));
        })
        .catch((err) => {
          if (err) {
            this.showErrorSnackbar(this.$t("error_deleting_invoice"));
          }
        });
    },

    load() {
      this.loading = true;
      warehouseService
        .getWarehouseDocuments({ document_type: "exit" })
        .then((res) => {
          this.documents = res.data;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_invoices"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    translatedDocuments() {
      return this.documents.map((item) => {
        return {
          ...item,
          number: this.translate(item.number),
          notes: this.translate(item.notes),
        };
      });
    },
    filteredDocuments() {
      return this.customSearch(
        this.translatedDocuments,
        this.headers,
        this.filters.search
      ).filter((document) => {
        const { from, to } = this.filters.dates;
        if (!from || !to) return true;

        const documentDate = new Date(document.document_date + "T12:00:00");
        const fromDate = new Date(from + "T00:00:00");
        const toDate = new Date(to + "T23:59:59");
        return documentDate >= fromDate && documentDate <= toDate;
      });
    },
    headers() {
      return [
        {
          text: this.$t("r_b"),
          value: "num",
          mobile: false,
          width: "90px",
          sortable: false,
        },
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("warehouse.document_number"), value: "id", cols: 6 },
        { text: this.$t("warehouse.shipping_date"), value: "document_date", cols: 6 },
        { text: this.$t("note"), value: "notes", cols: 9 },
        { text: this.$t("status_created"), value: "created_at", mobile: false, cols: 9 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
          width: "100px",
          align: "right",
        },
      ];
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    documentsByWarehouse() {
      const groups = {};
      this.filteredDocuments.forEach((item) => {
        const warehouseId = item.warehouse_id;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: this.translate(item.warehouse?.name) || this.$t("stock"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("issue"),
      ].join(" ");
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.load();
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.filteredDocuments.map((item, index) => {
                return {
                  ...item,
                  num: index + 1,
                };
              }),
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [
            {
              headers: this.headers,
              items: this.filteredDocuments.map((item, index) => {
                return {
                  ...item,
                  num: index + 1,
                };
              }),
            },
          ],
        });
      }
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 5;
}
</style>
